import React from "react";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getColorForSpecies } from "../colors";
import { formatLengthTick as formatLengthTickUtil } from "../formatters";

export default function StandardVsActualChart({ graphData, selectedUnit, selectedFish, isMobile }) {
  const formatLengthTick = (v) => formatLengthTickUtil(v, selectedUnit.value);
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };

const maxWeight = Math.max(
  ...graphData.map((d) => Math.max(d.standardWeight, d.actualWeight))
);
const minWeight = Math.min(
  ...graphData.map((d) => Math.min(d.standardWeight, d.actualWeight))
);
const isEnglishUnitsStd = selectedUnit.value === "English";

// Calculate unified range for both axes
let unifiedStart, unifiedEnd, unifiedStep;
let unifiedTicks;

if (isEnglishUnitsStd) {
  // English units: add 0.1 pound padding
  unifiedStart = Math.max(0, Math.floor(minWeight * 10) / 10 - 0.1);
  unifiedEnd = Math.ceil(maxWeight * 10) / 10 + 0.1;

  const unifiedRange = unifiedEnd - unifiedStart;

  // Calculate dynamic step size based on unified range
  if (unifiedRange < 1) {
    unifiedStep = 0.02;
  } else if (unifiedRange < 1.5) {
    unifiedStep = 0.05;
  } else if (unifiedRange <= 2) {
    unifiedStep = 0.1;
  } else if (unifiedRange <= 3) {
    unifiedStep = 0.15;
  } else if (unifiedRange <= 4) {
    unifiedStep = 0.2;
  } else if (unifiedRange <= 6) {
    unifiedStep = 0.25;
  } else if (unifiedRange <= 6) {
    unifiedStep = 0.3;
  } else if (unifiedRange <= 7) {
    unifiedStep = 0.35;
  } else if (unifiedRange <= 8) {
    unifiedStep = 0.4;
  } else if (unifiedRange <= 9) {
    unifiedStep = 0.45;
  } else if (unifiedRange <= 10) {
    unifiedStep = 0.5;
  } else if (unifiedRange <= 11) {
    unifiedStep = 0.55;
  } else if (unifiedRange <= 12) {
    unifiedStep = 0.6;
  } else if (unifiedRange <= 13) {
    unifiedStep = 0.65;
  } else if (unifiedRange <= 14) {
    unifiedStep = 0.7;
  } else if (unifiedRange <= 15) {
    unifiedStep = 0.75;
  } else if (unifiedRange <= 16) {
    unifiedStep = 0.8;
  } else if (unifiedRange <= 17) {
    unifiedStep = 0.85;
  } else if (unifiedRange <= 18) {
    unifiedStep = 0.9;
  } else if (unifiedRange <= 19) {
    unifiedStep = 9.5;
  } else if (unifiedRange <= 20) {
    unifiedStep = 1.0;
  } else if (unifiedRange <= 21) {
    unifiedStep = 1.05;
  } else if (unifiedRange <= 22) {
    unifiedStep = 1.1;
  } else if (unifiedRange <= 23) {
    unifiedStep = 1.15;
  }
  else if (unifiedRange <= 24) {
    unifiedStep = 1.2;
  }
  else {
    unifiedStep = 2.0;
  }
} else {
  // Metric units: add 50 gram padding
  unifiedStart = Math.max(0, Math.floor(minWeight / 50) * 50 - 50);
  unifiedEnd = Math.ceil(maxWeight / 50) * 50 + 50;

  const unifiedRange = unifiedEnd - unifiedStart;

  // Calculate dynamic step size based on unified range
  if (unifiedRange <= 500) {
    unifiedStep = 20;
  } else if (unifiedRange <= 750) {
    unifiedStep = 25;
  } else if (unifiedRange <= 1000) {
    unifiedStep = 50;
  } else if (unifiedRange <= 1500) {
    unifiedStep = 75;
  } else if (unifiedRange <= 2000) {
    unifiedStep = 100;
  } else if (unifiedRange <= 2500) {
    unifiedStep = 125;
  } else if (unifiedRange <= 3000) {
    unifiedStep = 150;
  } else if (unifiedRange <= 3500) {
    unifiedStep = 175;
  } else if (unifiedRange <= 4000) {
    unifiedStep = 200;
  } else if (unifiedRange <= 4500) {
    unifiedStep = 225;
  } else if (unifiedRange <= 5000) {
    unifiedStep = 250;
  } else if (unifiedRange <= 5500) {
    unifiedStep = 275;
  } else if (unifiedRange <= 6000) {
    unifiedStep = 300;
  } else if (unifiedRange <= 6500) {
    unifiedStep = 325;
  } else if (unifiedRange <= 7000) {
    unifiedStep = 350;
  } else if (unifiedRange <= 8000) {
    unifiedStep = 400;
  } else if (unifiedRange <= 10000) {
    unifiedStep = 500;
  } else if (unifiedRange <= 12000) {
    unifiedStep = 600;
  } else if (unifiedRange <= 14000) {
    unifiedStep = 700;
  } else if (unifiedRange <= 16000) {
    unifiedStep = 800;
  } else if (unifiedRange <= 18000) {
    unifiedStep = 900;
  } else if (unifiedRange <= 20000) {
    unifiedStep = 1000;
  } else if (unifiedRange <= 22000) {
    unifiedStep = 1100;
  } else if (unifiedRange <= 24000) {
    unifiedStep = 1200;
  } else if (unifiedRange <= 26000) {
    unifiedStep = 1300;
  } else if (unifiedRange <= 28000) {
    unifiedStep = 1400;
  } else if (unifiedRange <= 30000) {
    unifiedStep = 1500;
  } else if (unifiedRange <= 32000) {
    unifiedStep = 1600;
  } else if (unifiedRange <= 34000) {
    unifiedStep = 1700;
  } else if (unifiedRange <= 36000) {
    unifiedStep = 1800;
  } else if (unifiedRange <= 38000) {
    unifiedStep = 1900;
  } else if (unifiedRange <= 40000) {
    unifiedStep = 2000;
  } else {
    unifiedStep = 500;
  }
}

// Generate unified ticks for both axes
unifiedTicks = [];
for (let v = unifiedStart; v <= unifiedEnd + 1e-9; v += unifiedStep) {
  unifiedTicks.push(Number(v.toFixed(isEnglishUnitsStd ? 2 : 0)));
}

// Ensure minimum of 20 ticks for both axes
if (unifiedTicks.length < 20) {
  const additionalTicks = 20 - unifiedTicks.length;
  const extendedEnd = unifiedEnd + (additionalTicks * unifiedStep);
  unifiedTicks = [];
  for (let v = unifiedStart; v <= extendedEnd + 1e-9; v += unifiedStep) {
    unifiedTicks.push(Number(v.toFixed(isEnglishUnitsStd ? 2 : 0)));
  }
}

// Use the same ticks for both x and y axes
const xTicksStd = unifiedTicks;
const yTicksStd = unifiedTicks;
const uniqueSpeciesStd = [...new Set(graphData.map((d) => d.species))];
return (
  <ResponsiveContainer {...commonProps}>
    <ScatterChart data={graphData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
      <XAxis
        dataKey="standardWeight"
        label={{
          value: `Standard Weight (${selectedUnit.value === "English" ? "Pound" : "gram"
            })`,
          position: "outsideBottom",
          offset: isMobile ? 10 : 20,
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        type="number"
        ticks={xTicksStd}
        allowDecimals={true}
        tickFormatter={formatLengthTick}
        domain={[xTicksStd[0] || 0, xTicksStd[xTicksStd.length - 1] || 0]}
        angle={isMobile ? -45 : 0}
        textAnchor="middle"
        height={isMobile ? 80 : 60}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
      />
      <YAxis
        label={{
          value: `Actual Weight (${selectedUnit.value === "English" ? "Pound" : "gram"
            })`,
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        ticks={yTicksStd}
        allowDecimals={true}
        tickFormatter={formatLengthTick}
        domain={[yTicksStd[0] || 0, yTicksStd[yTicksStd.length - 1] || 0]}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
      />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;
          const data = payload[0].payload;
          const unit = selectedUnit.value === "English" ? "Pound" : "gram";
          const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
          const weightDiff = (data.actualWeight - data.standardWeight).toFixed(2);

          return (
            <div className="custom-tooltip" style={{
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '2px',
              padding: '8px 10px',
              fontSize: '13px',
              lineHeight: '1.5',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                {data.species}
                {data.isStocking ? " · Stocking" : ""}
              </div>
              <div style={{ display: 'grid', gap: '2px' }}>
                <div>
                  Standard Weight: <span style={{ color: '#666' }}>{data.standardWeight.toFixed(2)} {unit}</span>
                </div>
                <div>
                  Actual Weight: <span style={{ color: '#666' }}>{data.actualWeight.toFixed(2)} {unit}</span>
                </div>
                <div>
                  Length: <span style={{ color: '#666' }}>{data.length.toFixed(2)} {lengthUnit}</span>
                </div>
                <div>
                  Difference: <span style={{
                    color: weightDiff >= 0 ? '#2ecc71' : '#e74c3c'
                  }}>{weightDiff > 0 ? '+' : ''}{weightDiff} {unit}</span>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{
          paddingBottom: isMobile ? 20 : 40,
          fontSize: isMobile ? 12 : 14,
        }}
      />
      <ReferenceLine
        segment={[
          { x: 0, y: 0 },
          { x: xTicksStd[xTicksStd.length - 1] || 0, y: yTicksStd[yTicksStd.length - 1] || 0 },
        ]}
        stroke="#e74c3c"
        strokeWidth={2}
        strokeDasharray="5 5"
      />
      {uniqueSpeciesStd.map((species) => {
          const all = graphData.filter((d) => d.species === species);
          const samp = all.filter((d) => !d.isStocking);
          const stock = all.filter((d) => d.isStocking);
          const c = getColorForSpecies(species, uniqueSpeciesStd);
          return (
            <React.Fragment key={species}>
              {samp.length > 0 && (
                <Scatter
                  name={`${species} (${samp.length})`}
                  data={samp}
                  dataKey="actualWeight"
                  fill={c}
                  fillOpacity={0.6}
                />
              )}
              {stock.length > 0 && (
                <Scatter
                  name={`${species} (stocking) (${stock.length})`}
                  data={stock}
                  dataKey="actualWeight"
                  fill={c}
                  fillOpacity={0.75}
                  shape="diamond"
                />
              )}
            </React.Fragment>
          );
        })}
    </ScatterChart>
  </ResponsiveContainer>
);
}
